'use client';

import { useState, useCallback, useEffect, memo, useRef, forwardRef } from 'react';
import TrailingLink from '@/components/atoms/TrailingLink';
import SearchIcon from '@/components/atoms/SearchIcon';
import { HamburgerIcon } from '@/components/molecules/HamburgerIcon';
import { usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import { navigationData } from '@/lib/navigationData';
import type { MenuItem } from '@/types/navigation';
import { useScrollHide, useIsAtTop } from '../NavigationMenu/useScrollHide';
import { HeaderMotion, NavItemMotion, DropdownMotion } from '../NavigationMenu/HeaderMotion';
import { getPageLevel } from '@/lib/performance/pageLevel';
import { useHeaderVisibility } from '@/contexts/HeaderVisibilityContext';
import styles from '../NavigationMenu/NavigationMenu.module.scss';
import headerStyles from './Header.module.scss';

// Dynamic imports for performance
const MobileMenu = dynamic(() => import('../MobileMenu'), {
  loading: () => null,
  ssr: false,
});

const SearchModal = dynamic(
  () => import('../../shared/modals/SearchModal'),
  {
    loading: () => null,
    ssr: false,
  }
);

// Full-width dropdown component
const FullWidthDropdown = memo(forwardRef<HTMLDivElement, {
  activeItem: MenuItem | null;
  animationTier: 0 | 1 | 2 | 3;
  isHidden: boolean;
  onMouseEnter: () => void;
  onMouseLeave: (e: React.MouseEvent<HTMLDivElement>) => void;
}>(({
  activeItem,
  animationTier,
  isHidden,
  onMouseEnter,
  onMouseLeave
}, ref) => {
  const pathname = usePathname();
  
  if (!activeItem || !activeItem.children) {return null;}
  
  return (
    <DropdownMotion isOpen={!!activeItem} animationTier={animationTier}>
      <div
        className={`${styles?.fullWidthDropdown || ''} ${isHidden ? styles?.hidden || '' : ''}`}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role="navigation"
        aria-label="ドロップダウンメニュー"
        ref={ref}
      >
        <div className={styles?.dropdownContainer || ''}>
          <div className={styles?.dropdownGrid || ''}>
            {activeItem.children.filter(c => !c.id.endsWith('-menu-top') && !c.id.endsWith('-nav-left')).map((child) => (
              <div key={child.id} className={styles?.dropdownColumn || ''}>
                <TrailingLink
                  href={child.href}
                  className={`${styles?.dropdownLink || ''} ${
                    child.href && pathname.startsWith(child.href) ? styles?.active || '' : ''
                  }`}
                >
                  <span className={styles?.dropdownLinkText || ''}>{child.label}</span>
                </TrailingLink>
                {child.children && (
                  <ul className={styles?.dropdownSubList || ''}>
                    {child.children.filter(gc => !gc.id.endsWith('-menu-top') && !gc.id.endsWith('-nav-left')).map((grandchild) => (
                      <li key={grandchild.id}>
                        <TrailingLink
                          href={grandchild.href}
                          className={`${styles?.dropdownSubLink || ''} ${
                            pathname === grandchild.href ? styles?.active || '' : ''
                          } ${grandchild.id.endsWith('-menu-top') ? styles?.withArrow || '' : ''}`}
                        >
                          {grandchild.id.endsWith('-menu-top') && (
                            <span className={styles?.doubleLeftArrow || ''}>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M11 12L7 8l4-4M7 12L3 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                          )}
                          {grandchild.id.endsWith('-nav-left') && (
                            <span className={styles?.leftArrow || ''}>
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                          )}
                          {grandchild.label}
                        </TrailingLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </DropdownMotion>
  );
}));

FullWidthDropdown.displayName = 'FullWidthDropdown';

interface HeaderProps {
  className?: string;
}

// Main Header component with integrated navigation
export const Header = memo(({ className }: HeaderProps) => {
  const pathname = usePathname();
  const { hidden: isHidden } = useScrollHide();
  const isAtTop = useIsAtTop();
  const { isHeaderVisible } = useHeaderVisibility();
  // URL に基づきページ階層レベルを取得（アニメーション判定用）
  const pageLevel = getPageLevel(pathname);
  
  // State
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Ref to detect pointer interactions with dropdown
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const navContainerRef = useRef<HTMLDivElement | null>(null);
  
  // Hover delay for better UX
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // 透過ヘッダー機能（現状常に false）
  const isTransparent = false;
  
  // Callbacks
  const handleMouseEnter = useCallback((itemId: string) => {
    if (hoverTimeout) {clearTimeout(hoverTimeout);}
    // Immediately open the dropdown for better UX
    setActiveDropdown(itemId);
  }, [hoverTimeout]);
  
  const handleMouseLeave = useCallback(() => {
    if (hoverTimeout) {clearTimeout(hoverTimeout);}
    // Longer delay before closing to allow users to move to dropdown
    const timeout = setTimeout(() => {
      setActiveDropdown(null);
    }, 500); // Extended to 500ms for better UX
    setHoverTimeout(timeout);
  }, [hoverTimeout]);

  const handleDropdownMouseEnter = useCallback(() => {
    // Cancel any pending close timeout when entering dropdown
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  }, [hoverTimeout]);

  const handleDropdownMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // More reliable check for mouse movement to nav area
      const relatedTarget = e.relatedTarget as Element | null;
      if (
        navContainerRef.current &&
        relatedTarget &&
        relatedTarget instanceof Node &&
        (navContainerRef.current.contains(relatedTarget) || 
         navContainerRef.current === relatedTarget ||
         (relatedTarget instanceof Element && relatedTarget.closest(`.${styles?.desktopNav || ''}`)))
      ) {
        return;
      }
      // Use shorter delay for dropdown leave to improve responsiveness
      if (hoverTimeout) {clearTimeout(hoverTimeout);}
      const timeout = setTimeout(() => {
        setActiveDropdown(null);
      }, 150); // Shorter delay for dropdown leave
      setHoverTimeout(timeout);
    },
    [hoverTimeout]
  );

  const handleNavItemMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // More reliable check for mouse movement to dropdown
      const relatedTarget = e.relatedTarget as Element | null;
      if (
        dropdownRef.current &&
        relatedTarget &&
        relatedTarget instanceof Node &&
        (dropdownRef.current.contains(relatedTarget) || 
         dropdownRef.current === relatedTarget ||
         (relatedTarget instanceof Element && relatedTarget.closest(`.${styles?.fullWidthDropdown || ''}`)))
      ) {
        return;
      }
      handleMouseLeave();
    },
    [handleMouseLeave]
  );
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {clearTimeout(hoverTimeout);}
    };
  }, [hoverTimeout]);
  
  
  // Reset all states on route change for consistency
  useEffect(() => {
    setActiveDropdown(null);
    setIsMobileMenuOpen(false);
    
    // Reset scroll-related CSS variables
    if (typeof window !== 'undefined' && document.documentElement) {
      // Scroll to top on route change
      window.scrollTo(0, 0);
      
      // Reset CSS variables
      document.documentElement.style.setProperty('--scroll-progress', '0');
      document.documentElement.classList.remove('scrolled');
    }
  }, [pathname]);
  
  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent, itemId: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActiveDropdown(activeDropdown === itemId ? null : itemId);
    } else if (e.key === 'Escape') {
      setActiveDropdown(null);
    }
  }, [activeDropdown]);
  
  // Don't render header if it's explicitly hidden
  if (!isHeaderVisible) {
    return null;
  }

  return (
    <header className={`${headerStyles?.header || ''} ${className || ''}`}>
      <HeaderMotion 
        isVisible={!isHidden} 
        isTransparent={isTransparent}
        animationTier={0} // Always use tier 0 (homepage) behavior
      >
        <nav 
          className={`${styles?.navigationMenu || ''} ${
            !isAtTop ? styles?.scrolled || '' : ''
          } ${
            isHidden ? styles?.hidden || '' : ''
          } ${
            activeDropdown ? styles?.dropdownActive || '' : ''
          }`}
          role="navigation"
          aria-label="メインナビゲーション"
        >
          <div className={styles?.container || ''}>
            {/* Mobile Search Button (left) */}
            <button
              className={`${styles?.searchButton || ''} ${styles?.mobileOnly || ''}`}
              aria-label="検索"
              type="button"
              onClick={() => setIsSearchOpen(true)}
            >
              <SearchIcon />
            </button>
            
            {/* Logo */}
            <TrailingLink 
              href="/" 
              className={styles?.logo || ''}
              aria-label="BAYSGAiA ホーム"
            >
              BAYSGAiA
            </TrailingLink>
            
            {/* Desktop Navigation */}
            <div className={styles?.desktopNav || ''} ref={navContainerRef}>
              {navigationData.map((item, index) => (
                <NavItemMotion 
                  key={item.id} 
                  index={index} 
                  animationTier={0} // Always use tier 0 (homepage) behavior
                >
                  <div 
                    className={`${styles?.navItem || ''} ${activeDropdown === item.id ? styles?.isOpen || '' : ''}`}
                    onMouseEnter={() => item.children && handleMouseEnter(item.id)}
                    onMouseLeave={handleNavItemMouseLeave}
                    role="presentation"
                  >
                    <TrailingLink
                      href={item.href}
                      className={`${styles?.navLink || ''} ${
                        item.children ? styles?.hasChildren || '' : ''
                      }`}
                      aria-current={pathname === item.href ? 'page' : undefined}
                      aria-expanded={item.children ? activeDropdown === item.id : undefined}
                      aria-haspopup={item.children ? 'true' : undefined}
                      onKeyDown={(e) => item.children && handleKeyDown(e, item.id)}
                    >
                      {item.label}
                    </TrailingLink>
                    
                  </div>
                </NavItemMotion>
              ))}
            </div>
            
            {/* Actions */}
            <div className={styles?.actions || ''}>
              {/* Desktop Search Button */}
              <button
                className={`${styles?.searchButton || ''} ${styles?.desktopOnly || ''}`}
                aria-label="検索"
                type="button"
                onClick={() => setIsSearchOpen(true)}
              >
                <SearchIcon />
              </button>
              
              {/* Mobile Menu */}
              <HamburgerIcon 
                isOpen={isMobileMenuOpen}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={styles?.hamburgerButton || ''}
              />
            </div>
          </div>
          
          {/* Progress bar */}
          {pageLevel <= 1 && (
            <div 
              className={styles?.progressBar || ''} 
              style={{ '--scroll-progress': '0' } as React.CSSProperties} 
            />
          )}
        </nav>
      </HeaderMotion>
      {/* Full-width dropdown menu */}
      <FullWidthDropdown
        ref={dropdownRef}
        activeItem={navigationData.find(item => item.id === activeDropdown) || null}
        animationTier={0} // Always use tier 0 (homepage) behavior
        isHidden={isHidden}
        onMouseEnter={handleDropdownMouseEnter}
        onMouseLeave={handleDropdownMouseLeave}
      />
      {/* Skip to content link */}
      <a href="#main-content" className={styles?.skipLink || ''}>
        メインコンテンツへスキップ
      </a>
      {/* Modals */}
      <MobileMenu 
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)} 
      />
      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </header>
  );
});

Header.displayName = 'Header';

export default Header;